import { useState, useMemo } from "react";
import { type DisciplineSlug } from "@jumpbean/shared";

export type CharacterExpression =
  | "base"
  | "happy"
  | "encouraging"
  | "thinking"
  | "pointing"
  | "magic"
  | "typing"
  | "flying";

export type CharacterSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "hero" | "hero-xl";

export interface CharacterImageProps {
  /** The discipline/character to display */
  discipline: DisciplineSlug;
  /** The expression/state of the character */
  expression?: CharacterExpression;
  /** Size preset */
  size?: CharacterSize;
  /** Custom width (overrides size) */
  width?: number | string;
  /** Custom height (overrides size) */
  height?: number | string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show floating animation */
  floating?: boolean;
  /** Whether to show glow effect */
  glowing?: boolean;
  /** Alt text override */
  alt?: string;
}

const CHARACTER_NAMES: Record<DisciplineSlug, string> = {
  math: "luna",
  spelling: "petal",
  patterns: "prism",
  typing: "whiskers",
  programming: "pixel",
};

// Map expressions to fallbacks for characters that don't have all expressions
// Whiskers has: base, happy, encouraging, magic, typing (missing: pointing, thinking)
const EXPRESSION_FALLBACKS: Partial<Record<DisciplineSlug, Partial<Record<CharacterExpression, CharacterExpression>>>> = {
  typing: {
    pointing: "encouraging",
    thinking: "base",
  },
};

const CHARACTER_DISPLAY_NAMES: Record<DisciplineSlug, string> = {
  math: "Luna",
  spelling: "Petal",
  patterns: "Prism",
  typing: "Whiskers",
  programming: "Pixel",
};

// Size presets in pixels
const SIZE_PRESETS: Record<CharacterSize, number> = {
  xs: 32,
  sm: 48,
  md: 64,
  lg: 96,
  xl: 128,
  "2xl": 180,
  hero: 240,
  "hero-xl": 480,
};

// Character-specific glow colors
const GLOW_COLORS: Record<DisciplineSlug, string> = {
  math: "rgba(232, 160, 255, 0.5)", // Luna lavender
  spelling: "rgba(144, 238, 144, 0.5)", // Petal mint green
  patterns: "rgba(116, 185, 255, 0.5)", // Prism rainbow-blue
  typing: "rgba(74, 144, 217, 0.5)", // Whiskers royal blue
  programming: "rgba(255, 140, 66, 0.5)", // Pixel copper orange
};

// Fallback emoji for when images fail to load
const FALLBACK_EMOJI: Record<DisciplineSlug, string> = {
  math: "🦄",
  spelling: "🧚",
  patterns: "🐉",
  typing: "🐱",
  programming: "🦉",
};

/**
 * CharacterImage - Displays a character illustration with various states
 *
 * Features:
 * - Multiple character expressions
 * - Size presets for consistency
 * - Optional floating animation
 * - Optional glow effect matching character theme
 * - Graceful fallback to emoji if image fails
 */
export function CharacterImage({
  discipline,
  expression = "base",
  size = "md",
  width,
  height,
  className = "",
  floating = false,
  glowing = false,
  alt,
}: CharacterImageProps) {
  const [imageError, setImageError] = useState(false);

  const characterName = CHARACTER_NAMES[discipline];
  const displayName = CHARACTER_DISPLAY_NAMES[discipline];

  // Get the actual expression to use (with fallback for missing expressions)
  const actualExpression = useMemo(() => {
    const fallback = EXPRESSION_FALLBACKS[discipline]?.[expression];
    return fallback ?? expression;
  }, [discipline, expression]);

  // Build the image URL
  const imageUrl = useMemo(() => {
    return `/characters/${characterName}/images/${characterName}-${actualExpression}.svg`;
  }, [characterName, actualExpression]);

  // Get dimensions
  const sizeValue = SIZE_PRESETS[size];
  const finalWidth = width ?? sizeValue;
  const finalHeight = height ?? sizeValue;

  const glowColor = GLOW_COLORS[discipline];
  const emoji = FALLBACK_EMOJI[discipline];

  // Build style object
  const containerStyle: React.CSSProperties = {
    width: typeof finalWidth === "number" ? `${finalWidth}px` : finalWidth,
    height: typeof finalHeight === "number" ? `${finalHeight}px` : finalHeight,
  };

  if (glowing) {
    containerStyle.filter = `drop-shadow(0 0 20px ${glowColor}) drop-shadow(0 0 40px ${glowColor})`;
  }

  // If image fails, show emoji fallback
  if (imageError) {
    return (
      <div
        className={`
          flex items-center justify-center
          ${floating ? "animate-float" : ""}
          ${className}
        `}
        style={containerStyle}
      >
        <span
          className="drop-shadow-lg"
          style={{ fontSize: `${sizeValue * 0.7}px` }}
        >
          {emoji}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`
        relative
        ${floating ? "animate-float" : ""}
        ${className}
      `}
      style={containerStyle}
    >
      <img
        src={imageUrl}
        alt={alt || `${displayName} the ${getCharacterSpecies(discipline)}`}
        className="w-full h-full object-contain"
        onError={() => setImageError(true)}
        loading="lazy"
      />
    </div>
  );
}

function getCharacterSpecies(discipline: DisciplineSlug): string {
  const species: Record<DisciplineSlug, string> = {
    math: "Unicorn",
    spelling: "Fairy",
    patterns: "Dragon",
    typing: "Wizard Cat",
    programming: "Robot Owl",
  };
  return species[discipline];
}

// Export helper functions for use in other components
export function getCharacterImageUrl(
  discipline: DisciplineSlug,
  expression: CharacterExpression = "base"
): string {
  const characterName = CHARACTER_NAMES[discipline];
  return `/characters/${characterName}/images/${characterName}-${expression}.svg`;
}

export function getCharacterVideoUrl(discipline: DisciplineSlug): string {
  const characterName = CHARACTER_NAMES[discipline];
  return `/characters/${characterName}/videos/${characterName}-intro.mp4`;
}

export function getCharacterAnimationUrl(
  discipline: DisciplineSlug,
  animation: "idle" | "celebrate" | "encourage" | "flying" | "process"
): string {
  const characterName = CHARACTER_NAMES[discipline];
  return `/characters/${characterName}/animations/${characterName}-${animation}.json`;
}

export { CHARACTER_NAMES, CHARACTER_DISPLAY_NAMES, GLOW_COLORS, FALLBACK_EMOJI };

export default CharacterImage;
